import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  /** @type {AbortController | null} */
  #drawerAbort = null;

  connectedCallback() {
    super.connectedCallback();

    this.#drawerAbort = new AbortController();
    this.addEventListener('keyup', this.#onKeyUp);
    this.addEventListener('click', this.#onCatalogLinkClick, true);
    this.addEventListener('toggle', this.#loadDrawerBranch, true);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#drawerAbort?.abort();
    this.#drawerAbort = null;
    this.removeEventListener('keyup', this.#onKeyUp);
    this.removeEventListener('click', this.#onCatalogLinkClick, true);
    this.removeEventListener('toggle', this.#loadDrawerBranch, true);
  }

  /**
   * First expand of a catalog row fetches one level. Deeper rows do the same.
   * @param {Event} event
   */
  #loadDrawerBranch = async (event) => {
    const details = event.target;
    if (!(details instanceof HTMLDetailsElement) || !details.open) return;

    const content = details.querySelector(':scope > [data-catalog-drawer-url]');
    if (!(content instanceof HTMLElement)) return;

    const url = content.dataset.catalogDrawerUrl;
    if (!url) return;

    delete content.dataset.catalogDrawerUrl;
    content.textContent = content.dataset.catalogDrawerLoading || '';

    try {
      const response = await fetch(url, {
        signal: this.#drawerAbort?.signal,
        headers: { Accept: 'text/html' },
      });

      if (!response.ok) throw new Error(`Drawer branch fetch failed: ${response.status}`);

      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const tree = doc.querySelector('[data-catalog-drawer-tree]');
      content.innerHTML = tree?.innerHTML.trim() || '';

      if (!content.innerHTML) {
        details.open = false;
        details.classList.add('catalog-drawer__details--empty');
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;

      content.textContent = '';
      content.dataset.catalogDrawerUrl = url;
      details.open = false;
    }
  };

  /**
   * Title links inside a catalog summary navigate. The chevron stays the disclosure control.
   * @param {Event} event
   */
  #onCatalogLinkClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const link = target.closest('a.catalog-drawer__link');
    if (!(link instanceof HTMLAnchorElement) || !link.closest('summary')) return;

    event.preventDefault();
    event.stopPropagation();
    window.location.assign(link.href);
  };

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        reset(details);
        if (details === this.refs.details) {
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
        } else {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
